import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Container, Row, Col, Form, Alert, Spinner } from 'react-bootstrap';
import usePayment from '../hooks/usePayment';
import CheckoutForm from './checkoutForm';

const StripePayment = (props: {
    publishableKey: string,
    hostUrl: string,
    price: number,
    tipAmount?: number,
    documentId: string,
    isEstimate?: boolean,
    isDeposit?: boolean,
    connectedAccountId?: string | null,
    paymentError?: string | null,
    onErrorDismiss?: () => void,
    defaultEmail?: string
}) => {
    const stripePromise = useMemo(() => loadStripe(props.publishableKey), [props.publishableKey]);
    const { paymentIntent, error: paymentIntentError, loading } = usePayment(props.hostUrl, props.price, props.documentId, props.connectedAccountId || undefined, props.isDeposit, props.isEstimate, props.tipAmount);
    const [options, setOptions] = useState<any>();
    const [email, setEmail] = useState<string>(props.defaultEmail || "");
    const errorRef = useRef<HTMLDivElement>(null);

    // Update email if defaultEmail prop changes
    useEffect(() => {
        if (props.defaultEmail) {
            setEmail(props.defaultEmail);
        }
    }, [props.defaultEmail]);

    // Scroll to error when it appears
    useEffect(() => {
        if ((props.paymentError || paymentIntentError) && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [props.paymentError, paymentIntentError]);

    useEffect(() => {
        console.log("props:", props);
        if (paymentIntent && paymentIntent.clientSecret) {
            const options = {
                clientSecret: paymentIntent.clientSecret
            };

            setOptions(options);

            console.log("Payment Intent:", paymentIntent);
        }
    }, [paymentIntent]);

    const shouldShowPaymentForm = options && options.clientSecret && (props.isEstimate === undefined || props.isEstimate === false || props.isDeposit === true);

    return (
        <div className="bg-white rounded-lg p-5">
            {loading && (
                <Container>
                    <Row className="justify-content-center py-4">
                        <Col xs="auto" className="text-center">
                            <Spinner animation="border" variant="primary" className="me-2" />
                            <span className="text-muted">Loading payment form...</span>
                        </Col>
                    </Row>
                </Container>
            )}

            {paymentIntentError && (
                <Container>
                    <Row className="mb-4">
                        <Col>
                            <div ref={errorRef}>
                                <Alert variant="danger">
                                    <Alert.Heading>
                                        <i className="fas fa-exclamation-circle me-2"></i>
                                        Payment Setup Error
                                    </Alert.Heading>
                                    <p className="mb-0">{paymentIntentError}</p>
                                </Alert>
                            </div>
                        </Col>
                    </Row>
                </Container>
            )}

            {shouldShowPaymentForm && (
                <Container>
                    <Row className="mb-4">
                        <Col>
                            <h3 className="text-xl font-medium text-gray-800 mb-4">
                                {props.isDeposit ? 'Solar Screen Deposit Payment' : 'Payment Information'}
                            </h3>

                            {props.isDeposit && (
                                <div className="alert alert-info mb-4">
                                    <div className="d-flex align-items-center">
                                        <div className="me-3">
                                            <i className="fas fa-credit-card fa-lg"></i>
                                        </div>
                                        <div>
                                            <strong>50% Deposit Payment:</strong> ${props.price}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <Form.Group className="mb-4">
                                <Form.Label>Email for Receipt</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="your.email@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="p-3 border rounded-md"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    {props.paymentError && (
                        <Row className="mb-4">
                            <Col>
                                <div ref={errorRef}>
                                    <Alert
                                        variant="danger"
                                        onClose={props.onErrorDismiss}
                                        dismissible
                                    >
                                        <Alert.Heading>
                                            <i className="fas fa-exclamation-circle me-2"></i>
                                            Payment Failed
                                        </Alert.Heading>
                                        <p className="mb-0">
                                            {props.paymentError}
                                        </p>
                                    </Alert>
                                </div>
                            </Col>
                        </Row>
                    )}
                    <Row className="mb-5">
                        <Col>
                            <Elements stripe={stripePromise} options={options} key={paymentIntent?.clientSecret}>
                                <CheckoutForm
                                    email={email}
                                    isDeposit={props.isDeposit}
                                    tipAmount={props.tipAmount}
                                    totalAmount={props.price}
                                />
                            </Elements>
                        </Col>
                    </Row>
                </Container>
            )}
        </div>
    );
};

export default StripePayment;
